// Copyright (c) 2026 Yunus YILDIZ — SPDX-License-Identifier: BUSL-1.1

import { apiClient } from './client'
import type {
  Organization,
  AccountProfile,
  TeamMember,
  ActiveSession,
  PendingInvite,
  MyPendingInvite,
  NotificationPreferences,
  NotificationPreferencesUpdate,
  InAppNotificationsListResponse,
  User,
  AuthTokens,
} from '@/types'

export const accountApi = {
  getOrganization: () => apiClient.get<Organization>('/v1/account/organization'),

  updateOrganization: (payload: { name?: string }) =>
    apiClient.patch<Organization>('/v1/account/organization', payload),

  getProfile: () => apiClient.get<AccountProfile>('/v1/account/profile'),

  updateProfile: (payload: { name?: string }) =>
    apiClient.patch<AccountProfile>('/v1/account/profile', payload),

  changePassword: (payload: { currentPassword: string; newPassword: string }) =>
    apiClient.post<void>('/v1/account/profile/password', payload),

  getTeam: () => apiClient.get<{ members: TeamMember[] }>('/v1/account/team'),

  updateMemberRole: (userId: string, role: string) =>
    apiClient.patch<TeamMember>(`/v1/account/team/${userId}`, { role }),

  removeMember: (userId: string) => apiClient.delete(`/v1/account/team/${userId}`),

  // Invites
  getInvites: () => apiClient.get<{ invites: PendingInvite[] }>('/v1/account/team/invites'),

  createInvite: (payload: { email: string; role: string }) =>
    apiClient.post<PendingInvite>('/v1/account/team/invites', payload),

  cancelInvite: (inviteId: string) => apiClient.delete(`/v1/account/team/invites/${inviteId}`),

  getMyPendingInvites: () =>
    apiClient.get<{ invites: MyPendingInvite[] }>('/v1/account/team/invites/mine'),

  acceptInviteAuthenticated: (body: { token: string } | { inviteId: string }) =>
    apiClient.post<{ tokens: AuthTokens; user: User }>('/v1/account/team/invite/accept', body),

  // Sessions
  getSessions: () => apiClient.get<{ sessions: ActiveSession[] }>('/v1/account/sessions'),

  revokeSession: (sessionId: string) => apiClient.delete(`/v1/account/sessions/${sessionId}`),

  revokeAllSessions: () => apiClient.post<void>('/v1/account/sessions/revoke-all', {}),

  mfaTotpSetup: () =>
    apiClient.post<{ otpauthUrl: string; qrDataUrl: string; secret: string }>(
      '/v1/account/profile/mfa/totp/setup',
      {},
    ),

  mfaTotpVerify: (code: string) =>
    apiClient.post<{ recoveryCodes: string[]; totpEnabled: boolean }>(
      '/v1/account/profile/mfa/totp/verify',
      { code },
    ),

  mfaTotpDisable: (payload: { password?: string; code?: string }) =>
    apiClient.post<void>('/v1/account/profile/mfa/totp/disable', payload),

  getNotificationPreferences: () =>
    apiClient.get<NotificationPreferences>('/v1/account/profile/notification-preferences'),

  updateNotificationPreferences: (payload: NotificationPreferencesUpdate) =>
    apiClient.patch<NotificationPreferences>('/v1/account/profile/notification-preferences', payload),

  getInAppNotifications: (params?: {
    limit?: number
    cursor?: string
    unreadOnly?: boolean
    category?: string
  }) => apiClient.get<InAppNotificationsListResponse>('/v1/account/notifications', params),

  getInAppUnreadCount: () =>
    apiClient.get<{ unread: number }>('/v1/account/notifications/unread-count'),

  markInAppNotificationRead: (id: string) =>
    apiClient.patch<void>(`/v1/account/notifications/${id}/read`),

  markAllInAppNotificationsRead: () =>
    apiClient.patch<{ marked: number }>('/v1/account/notifications/read-all'),

  getNotificationWsToken: () =>
    apiClient.post<{ token: string; expiresAt: string }>('/v1/account/notifications/ws-token', {}),

  createOrgAnnouncement: (payload: {
    title: string
    body: string
    actionPath?: string
    forceDelivery?: boolean
  }) =>
    apiClient.post<{ broadcastId: string }>('/v1/account/organization/announcements', payload),
}
